import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Copy,
  Trash2,
  Archive,
  ArchiveRestore,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  X,
  Check,
  Mic,
  Tags,
  Calendar,
  Building2,
  Layers,
  CheckSquare,
  Square,
  AlertTriangle,
  RefreshCw,
  CheckCircle,
  Loader2,
  Phone,
  Clock
} from 'lucide-react'
import { avatarApi, voiceSetApi, licenseApi, engineApi, soundApi, dialerApi, monitoringApi } from '../services/api'
import './Avatars.css'

// Animation variants (Dashboard style)
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.4, 0, 0.2, 1] }
  }
}

const tableRowVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.03, duration: 0.3 }
  })
}

// Items per page options
const PAGE_SIZES = [50, 100, 200, 500]

// Searchable Select Component
const SearchableSelect = ({ options, value, onChange, placeholder, field, openDropdown, setOpenDropdown }) => {
  const [localSearch, setLocalSearch] = useState('')
  const isOpen = openDropdown === field

  const filteredOptions = options.filter(opt => {
    const label = typeof opt === 'object' ? opt.label : opt
    return label.toLowerCase().includes(localSearch.toLowerCase())
  })

  const handleSelect = (opt) => {
    const val = typeof opt === 'object' ? opt.value : opt
    onChange(val)
    setOpenDropdown(null)
    setLocalSearch('')
  }

  const handleToggle = (e) => {
    e.stopPropagation()
    if (isOpen) {
      setOpenDropdown(null)
      setLocalSearch('')
    } else {
      setOpenDropdown(field)
    }
  }

  const getDisplayValue = () => {
    if (!value && value !== 0) return placeholder
    const opt = options.find(o => (typeof o === 'object' ? o.value : o) === value)
    return opt ? (typeof opt === 'object' ? opt.label : opt) : value
  }

  return (
    <div className="searchable-select" onClick={(e) => e.stopPropagation()}>
      <div
        className={`searchable-select-trigger ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
      >
        <span className={`searchable-select-value ${!value && value !== 0 ? 'placeholder' : ''}`}>
          {getDisplayValue()}
        </span>
        <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </div>
      {isOpen && (
        <div className="searchable-select-dropdown" onClick={(e) => e.stopPropagation()}>
          <div className="searchable-select-search">
            <input
              type="text"
              placeholder="Search..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="searchable-select-options">
            {filteredOptions.length === 0 ? (
              <div className="searchable-select-option no-results">No matches found</div>
            ) : (
              filteredOptions.map((opt, idx) => {
                const optValue = typeof opt === 'object' ? opt.value : opt
                const optLabel = typeof opt === 'object' ? opt.label : opt
                return (
                  <div
                    key={`${optValue}-${idx}`}
                    className={`searchable-select-option ${optValue === value ? 'selected' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleSelect(opt) }}
                  >
                    {optLabel}
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Avatars() {
  const navigate = useNavigate()

  // Data states
  const [avatars, setAvatars] = useState([])
  const [voiceSets, setVoiceSets] = useState([])
  const [dialers, setDialers] = useState([])
  const [botStatus, setBotStatus] = useState([])
  const [licenses, setLicenses] = useState([])
  const [loading, setLoading] = useState(true)

  // Filter states
  const [searchQuery, setSearchQuery] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [filterCompany, setFilterCompany] = useState('')
  const [filterVoiceSet, setFilterVoiceSet] = useState('')
  const [filterVoiceMode, setFilterVoiceMode] = useState('')
  const [filterKeywordMode, setFilterKeywordMode] = useState('')
  const [filterDialer, setFilterDialer] = useState('')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)

  // Selection states
  const [selectedAvatars, setSelectedAvatars] = useState([])
  const [selectAll, setSelectAll] = useState(false)

  // Menu & Modal states
  const [activeMenu, setActiveMenu] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState('add')
  const [currentAvatar, setCurrentAvatar] = useState(null)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [cloning, setCloning] = useState(false)
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })
  const [formData, setFormData] = useState({
    name: '',
    voice_mode: 'modern',
    keyword_mode: 'modern',
    voice_set_id: ''
  })

  // Searchable dropdown state
  const [openDropdown, setOpenDropdown] = useState(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [avatarsRes, voiceSetsRes, dialersRes, botStatusRes, licensesRes] = await Promise.allSettled([
        avatarApi.getAll(),
        voiceSetApi.getAll(),
        dialerApi.getAll(),
        monitoringApi.getAllStatus(),
        licenseApi.getAll()
      ])

      const avatarsData = avatarsRes.status === 'fulfilled' ? avatarsRes.value.data : []
      const voiceSetsData = voiceSetsRes.status === 'fulfilled' ? voiceSetsRes.value.data : []
      const dialersData = dialersRes.status === 'fulfilled' ? dialersRes.value.data : []
      const botStatusData = botStatusRes.status === 'fulfilled' ? botStatusRes.value.data : []
      const licensesData = licensesRes.status === 'fulfilled' ? licensesRes.value.data : []

      setAvatars(Array.isArray(avatarsData) ? avatarsData : [])
      setVoiceSets(Array.isArray(voiceSetsData) ? voiceSetsData : [])
      setDialers(Array.isArray(dialersData) ? dialersData : [])
      setBotStatus(Array.isArray(botStatusData) ? botStatusData : [])
      setLicenses(Array.isArray(licensesData) ? licensesData : [])
    } catch (error) {
      console.error('Failed to load data:', error)
      setAvatars([])
      setVoiceSets([])
      setDialers([])
      setBotStatus([])
      setLicenses([])
    } finally {
      setLoading(false)
    }
  }

  // Create avatar_id -> dialer lookup
  const avatarDialerMap = useMemo(() => {
    const map = {}
    dialers.forEach(dialer => {
      if (dialer.avatar_id) {
        map[dialer.avatar_id] = dialer
      }
    })
    return map
  }, [dialers])

  // Get dialer for an avatar
  const getDialerForAvatar = (avatarId) => {
    return avatarDialerMap[avatarId] || null
  }

  // Create avatar_id -> last used timestamp lookup from bot status
  const avatarLastUsedMap = useMemo(() => {
    const map = {}
    botStatus.forEach(bot => {
      if (bot.avatar_id) {
        // Use last_heartbeat or updated_at as the timestamp
        const timestamp = bot.last_heartbeat || bot.updated_at || bot.last_seen
        if (timestamp) {
          const existingTime = map[bot.avatar_id]
          const newTime = new Date(timestamp).getTime()
          if (!existingTime || newTime > existingTime) {
            map[bot.avatar_id] = newTime
          }
        }
      }
    })
    return map
  }, [botStatus])

  // Get last used time for an avatar (returns formatted string)
  const getLastUsed = (avatarId) => {
    const timestamp = avatarLastUsedMap[avatarId]
    if (!timestamp) return null

    const now = Date.now()
    const diff = now - timestamp

    // Less than 2 minutes = "Active"
    if (diff < 2 * 60 * 1000) return { text: 'Active', isActive: true }

    // Less than 1 hour
    if (diff < 60 * 60 * 1000) {
      const mins = Math.floor(diff / 60000)
      return { text: `${mins}m ago`, isActive: false }
    }

    // Less than 24 hours
    if (diff < 24 * 60 * 60 * 1000) {
      const hours = Math.floor(diff / 3600000)
      return { text: `${hours}h ago`, isActive: false }
    }

    // Less than 7 days
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      const days = Math.floor(diff / 86400000)
      return { text: `${days}d ago`, isActive: false }
    }

    // Show date
    const date = new Date(timestamp)
    return { text: date.toLocaleDateString(), isActive: false }
  }

  // Create avatar_id -> license count lookup
  const licensesCountMap = useMemo(() => {
    const map = {}
    licenses.forEach(license => {
      if (license.avatar_id) {
        map[license.avatar_id] = (map[license.avatar_id] || 0) + 1
      }
    })
    return map
  }, [licenses])

  // Get license count for an avatar
  const getLicenseCount = (avatarId) => {
    return licensesCountMap[avatarId] || 0
  }

  // Extract unique companies from avatar names (assumes format: "CompanyName - AvatarName" or "CompanyName_AvatarName")
  const companies = useMemo(() => {
    const companySet = new Set()
    avatars.forEach(avatar => {
      // Try to extract company from name patterns
      const separators = [' - ', '_', ' | ']
      for (const sep of separators) {
        if (avatar.name.includes(sep)) {
          companySet.add(avatar.name.split(sep)[0].trim())
          break
        }
      }
    })
    return Array.from(companySet).sort()
  }, [avatars])

  // Filter avatars
  const filteredAvatars = useMemo(() => {
    return avatars.filter(avatar => {
      // Search filter - matches ID, Name, or Dialer name
      if (searchQuery) {
        const query = searchQuery.toLowerCase().trim()
        const matchesId = String(avatar.id).includes(query)
        const matchesName = avatar.name.toLowerCase().includes(query)
        const dialer = getDialerForAvatar(avatar.id)
        const matchesDialer = dialer?.name?.toLowerCase().includes(query)

        if (!matchesId && !matchesName && !matchesDialer) {
          return false
        }
      }

      // Archived filter
      if (showArchived) {
        if (!avatar.archived) return false
      } else {
        if (avatar.archived) return false
      }

      // Company filter
      if (filterCompany) {
        const hasCompany = avatar.name.toLowerCase().startsWith(filterCompany.toLowerCase())
        if (!hasCompany) return false
      }

      // Voice set filter
      if (filterVoiceSet) {
        if (String(avatar.voice_set_id) !== filterVoiceSet) return false
      }

      // Voice mode filter
      if (filterVoiceMode) {
        if ((avatar.voice_mode || 'classic') !== filterVoiceMode) return false
      }

      // Keyword mode filter
      if (filterKeywordMode) {
        if ((avatar.keyword_mode || 'legacy') !== filterKeywordMode) return false
      }

      // Dialer filter
      if (filterDialer) {
        const dialer = getDialerForAvatar(avatar.id)
        if (!dialer || String(dialer.id) !== filterDialer) return false
      }

      // Date filters (assuming avatar has created_at or similar field)
      if (filterDateFrom && avatar.created_at) {
        if (new Date(avatar.created_at) < new Date(filterDateFrom)) return false
      }
      if (filterDateTo && avatar.created_at) {
        if (new Date(avatar.created_at) > new Date(filterDateTo)) return false
      }

      return true
    })
  }, [avatars, searchQuery, showArchived, filterCompany, filterVoiceSet, filterVoiceMode, filterKeywordMode, filterDialer, filterDateFrom, filterDateTo, avatarDialerMap])

  // Pagination
  const totalPages = Math.ceil(filteredAvatars.length / pageSize)
  const paginatedAvatars = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredAvatars.slice(start, start + pageSize)
  }, [filteredAvatars, currentPage, pageSize])

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, showArchived, filterCompany, filterVoiceSet, filterVoiceMode, filterKeywordMode, filterDialer, filterDateFrom, filterDateTo, pageSize])

  // Selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAvatars([])
      setSelectAll(false)
    } else {
      setSelectedAvatars(paginatedAvatars.map(a => a.id))
      setSelectAll(true)
    }
  }

  const handleSelectAllFiltered = () => {
    setSelectedAvatars(filteredAvatars.map(a => a.id))
  }

  const handleSelect = (id) => {
    setSelectedAvatars(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    )
    setSelectAll(false)
  }

  const clearSelection = () => {
    setSelectedAvatars([])
    setSelectAll(false)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('')
    setFilterCompany('')
    setFilterVoiceSet('')
    setFilterVoiceMode('')
    setFilterKeywordMode('')
    setFilterDialer('')
    setFilterDateFrom('')
    setFilterDateTo('')
  }

  const hasActiveFilters = filterCompany || filterVoiceSet || filterVoiceMode || filterKeywordMode || filterDialer || filterDateFrom || filterDateTo

  // Modal handlers
  const openModal = (mode, avatar = null) => {
    setModalMode(mode)
    setCurrentAvatar(avatar)
    if (avatar && (mode === 'edit' || mode === 'clone')) {
      setFormData({
        name: mode === 'clone' ? `${avatar.name} (Copy)` : avatar.name,
        voice_mode: 'modern',
        keyword_mode: 'modern',
        voice_set_id: avatar.voice_set_id || ''
      })
    } else {
      setFormData({
        name: '',
        voice_mode: 'modern',
        keyword_mode: 'modern',
        voice_set_id: ''
      })
    }
    setShowModal(true)
    setActiveMenu(null)
  }

  const closeModal = () => {
    setShowModal(false)
    setCurrentAvatar(null)
    setOpenDropdown(null)
  }

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type })
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (modalMode === 'add') {
        await avatarApi.create(formData)
        showToast('Avatar created successfully')
      } else if (modalMode === 'edit') {
        await avatarApi.update(currentAvatar.id, formData)
        showToast('Avatar updated successfully')
      } else if (modalMode === 'clone') {
        await handleClone()
        return // handleClone manages its own modal closing
      }
      loadData()
      closeModal()
    } catch (error) {
      console.error('Failed to save avatar:', error)
      showToast('Failed to save avatar', 'error')
      loadData()
      closeModal()
    }
  }

  const handleClone = async () => {
    if (cloning) return
    setCloning(true)

    try {
      const avatarOrigin = currentAvatar

      // 1. Create new avatar with copied settings
      const avatarDestination = {
        name: formData.name,
        company: avatarOrigin.company,
        campaign: avatarOrigin.campaign,
        is_archived: false,
        voice_mode: 'modern',
        voice_set_id: formData.voice_set_id || avatarOrigin.voice_set_id || null,
        keyword_mode: 'modern'
      }

      const newAvatarRes = await avatarApi.create(avatarDestination)
      const newAvatarId = newAvatarRes.data?.id || newAvatarRes.data?.insertId

      if (!newAvatarId) {
        throw new Error('Failed to get new avatar ID')
      }

      // 2. Copy engine actions
      const engineActionsRes = await engineApi.getActions(avatarOrigin.id)
      const engineActions = engineActionsRes.data || []

      for (const action of engineActions) {
        const actionCopy = { ...action, avatar_id: newAvatarId }
        delete actionCopy.id
        await engineApi.createAction(actionCopy)
      }

      // 3. Copy sound records (updating filename paths)
      const soundsRes = await soundApi.getFromTable(avatarOrigin.id)
      const sounds = soundsRes.data || []

      for (const sound of sounds) {
        const soundCopy = {
          ...sound,
          avatar_id: newAvatarId,
          filename: sound.filename ? sound.filename.replace(`${avatarOrigin.id}/`, `${newAvatarId}/`) : sound.filename
        }
        delete soundCopy.id
        await soundApi.create(soundCopy)
      }

      showToast(`Avatar cloned successfully! ${engineActions.length} actions and ${sounds.length} sounds copied.`)
      loadData()
      closeModal()
    } catch (error) {
      console.error('Failed to clone avatar:', error)
      showToast('Failed to clone avatar: ' + (error.message || 'Unknown error'), 'error')
    } finally {
      setCloning(false)
    }
  }

  const handleDelete = async () => {
    try {
      const licensesRes = await licenseApi.getAll()
      const hasLicenses = licensesRes.data?.some(l => l.avatar_id === currentAvatar.id)
      if (hasLicenses) {
        showToast('Cannot delete avatar with associated licenses.', 'error')
        return
      }
      await avatarApi.delete(currentAvatar.id)
      showToast('Avatar deleted successfully')
      loadData()
      closeModal()
    } catch (error) {
      console.error('Failed to delete avatar:', error)
      showToast('Failed to delete avatar', 'error')
      loadData()
      closeModal()
    }
  }

  const handleArchive = async (avatar) => {
    try {
      if (avatar.archived) {
        await avatarApi.unarchive(avatar.id)
        showToast('Avatar restored successfully')
      } else {
        await avatarApi.archive(avatar.id)
        showToast('Avatar archived successfully')
      }
      loadData()
    } catch (error) {
      console.error('Failed to archive avatar:', error)
      showToast('Failed to update avatar', 'error')
    }
    setActiveMenu(null)
  }

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedAvatars.length === 0) return

    setBulkDeleting(true)
    try {
      // Check for licenses
      const licensesRes = await licenseApi.getAll()
      const licenses = licensesRes.data || []
      const avatarsWithLicenses = selectedAvatars.filter(id =>
        licenses.some(l => l.avatar_id === id)
      )

      if (avatarsWithLicenses.length > 0) {
        showToast(`${avatarsWithLicenses.length} avatars have licenses and will be skipped.`, 'error')
      }

      const avatarsToDelete = selectedAvatars.filter(id =>
        !licenses.some(l => l.avatar_id === id)
      )

      // Delete in batches
      const batchSize = 10
      for (let i = 0; i < avatarsToDelete.length; i += batchSize) {
        const batch = avatarsToDelete.slice(i, i + batchSize)
        await Promise.all(batch.map(id => avatarApi.delete(id)))
      }

      showToast(`${avatarsToDelete.length} avatars deleted successfully`)
      setSelectedAvatars([])
      setSelectAll(false)
      setShowBulkDeleteModal(false)
      loadData()
    } catch (error) {
      console.error('Failed to bulk delete:', error)
      showToast('Some avatars could not be deleted.', 'error')
    } finally {
      setBulkDeleting(false)
    }
  }

  // Bulk archive handler
  const handleBulkArchive = async () => {
    if (selectedAvatars.length === 0) return

    try {
      const batchSize = 10
      for (let i = 0; i < selectedAvatars.length; i += batchSize) {
        const batch = selectedAvatars.slice(i, i + batchSize)
        await Promise.all(batch.map(id => avatarApi.archive(id)))
      }
      showToast(`${selectedAvatars.length} avatars archived successfully`)
      setSelectedAvatars([])
      setSelectAll(false)
      loadData()
    } catch (error) {
      console.error('Failed to bulk archive:', error)
      showToast('Failed to archive some avatars', 'error')
    }
  }

  // Get voice set name by id
  const getVoiceSetName = (id) => {
    const vs = voiceSets.find(v => v.id === id)
    return vs ? vs.name : '-'
  }

  return (
    <motion.div
      className="avatars-page compact"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb */}
      <motion.div className="breadcrumb" variants={itemVariants}>
        <Link to="/">Dashboard</Link>
        <ChevronRight size={16} />
        <span>Avatars</span>
      </motion.div>

      {/* Header */}
      <motion.div className="page-header" variants={itemVariants}>
        <div>
          <h1 className="page-title">Avatars</h1>
          <p className="page-subtitle">
            {filteredAvatars.length.toLocaleString()} of {avatars.length.toLocaleString()} avatars
            {hasActiveFilters && ' (filtered)'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => openModal('add')}>
            <Plus size={16} /> New Avatar
          </button>
        </div>
      </motion.div>

      {/* Toolbar */}
      <motion.div className="toolbar compact" variants={itemVariants}>
        <div className="toolbar-left">
          <div className="input-with-icon compact">
            <Search size={16} className="icon" />
            <input
              type="text"
              className="input sm"
              placeholder="Search by ID, name, or dialer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 280 }}
            />
          </div>
          <button
            className={`btn btn-secondary btn-sm ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={14} />
            Filters
            {hasActiveFilters && (
              <span className="filter-badge">
                {[filterCompany, filterVoiceSet, filterVoiceMode, filterKeywordMode, filterDialer, filterDateFrom, filterDateTo].filter(Boolean).length}
              </span>
            )}
          </button>
          <button
            className={`btn btn-sm ${showArchived ? 'btn-warning' : 'btn-secondary'}`}
            onClick={() => setShowArchived(!showArchived)}
          >
            <Archive size={14} />
            {showArchived ? 'Showing Archived' : 'Show Archived'}
          </button>
        </div>
        <div className="toolbar-right">
          <select
            className="input sm"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            style={{ width: 100 }}
          >
            {PAGE_SIZES.map(size => (
              <option key={size} value={size}>{size} / page</option>
            ))}
          </select>
        </div>
      </motion.div>

      {/* Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            className="filters-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="filters-grid">
              <div className="filter-group">
                <label><Building2 size={12} /> Company</label>
                <select
                  className="input sm"
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                >
                  <option value="">All Companies</option>
                  {companies.map(company => (
                    <option key={company} value={company}>{company}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label><Mic size={12} /> Voice Set</label>
                <select
                  className="input sm"
                  value={filterVoiceSet}
                  onChange={(e) => setFilterVoiceSet(e.target.value)}
                >
                  <option value="">All Voice Sets</option>
                  {voiceSets.map(vs => (
                    <option key={vs.id} value={vs.id}>{vs.name}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label><Mic size={12} /> Voice Mode</label>
                <select
                  className="input sm"
                  value={filterVoiceMode}
                  onChange={(e) => setFilterVoiceMode(e.target.value)}
                >
                  <option value="">All Modes</option>
                  <option value="classic">Classic</option>
                  <option value="modern">Modern</option>
                </select>
              </div>

              <div className="filter-group">
                <label><Tags size={12} /> Keyword Mode</label>
                <select
                  className="input sm"
                  value={filterKeywordMode}
                  onChange={(e) => setFilterKeywordMode(e.target.value)}
                >
                  <option value="">All Modes</option>
                  <option value="legacy">Legacy</option>
                  <option value="modern">Modern</option>
                </select>
              </div>

              <div className="filter-group">
                <label><Phone size={12} /> Dialer</label>
                <select
                  className="input sm"
                  value={filterDialer}
                  onChange={(e) => setFilterDialer(e.target.value)}
                >
                  <option value="">All Dialers</option>
                  {dialers.map(dialer => (
                    <option key={dialer.id} value={dialer.id}>{dialer.name}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label><Calendar size={12} /> Created From</label>
                <input
                  type="date"
                  className="input sm"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label><Calendar size={12} /> Created To</label>
                <input
                  type="date"
                  className="input sm"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                />
              </div>
            </div>

            {hasActiveFilters && (
              <div className="filters-actions">
                <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
                  <X size={14} /> Clear Filters
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Actions Bar */}
      <AnimatePresence>
        {selectedAvatars.length > 0 && (
          <motion.div
            className="bulk-actions-bar"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="bulk-info">
              <CheckSquare size={16} />
              <span><strong>{selectedAvatars.length}</strong> selected</span>
              {selectedAvatars.length < filteredAvatars.length && (
                <button className="btn btn-ghost btn-xs" onClick={handleSelectAllFiltered}>
                  Select all {filteredAvatars.length}
                </button>
              )}
              <button className="btn btn-ghost btn-xs" onClick={clearSelection}>
                Clear
              </button>
            </div>
            <div className="bulk-actions">
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleBulkArchive}
              >
                <Archive size={14} /> Archive
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => setShowBulkDeleteModal(true)}
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <motion.div className="table-container compact" variants={itemVariants}>
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading avatars...</p>
          </div>
        ) : filteredAvatars.length === 0 ? (
          <div className="empty-state">
            <Layers size={48} />
            <h3>No avatars found</h3>
            <p>Try adjusting your filters or create a new avatar</p>
          </div>
        ) : (
          <>
            <table className="table compact">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={selectAll && paginatedAvatars.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={{ width: 50 }}>ID</th>
                  <th>Name</th>
                  <th style={{ width: 140 }}>Dialer</th>
                  <th style={{ width: 50 }}>Bots</th>
                  <th style={{ width: 90 }}>Last Used</th>
                  <th style={{ width: 100 }}>Voice</th>
                  <th style={{ width: 100 }}>Keywords</th>
                  <th style={{ width: 150 }}>Voice Set</th>
                  <th style={{ width: 80 }}>Status</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {paginatedAvatars.map((avatar, index) => {
                  const dialer = getDialerForAvatar(avatar.id)
                  const lastUsed = getLastUsed(avatar.id)
                  const licenseCount = getLicenseCount(avatar.id)
                  return (
                    <motion.tr
                      key={avatar.id}
                      className={selectedAvatars.includes(avatar.id) ? 'selected' : ''}
                      variants={tableRowVariants}
                      initial="hidden"
                      animate="visible"
                      custom={index}
                      whileHover={{ backgroundColor: 'var(--bg-card-hover)' }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedAvatars.includes(avatar.id)}
                          onChange={() => handleSelect(avatar.id)}
                        />
                      </td>
                      <td className="text-muted">{avatar.id}</td>
                      <td>
                        <div
                          className="avatar-name-cell"
                          onClick={() => navigate(`/avatars/${avatar.id}`)}
                        >
                          <span className="avatar-name-link">{avatar.name}</span>
                        </div>
                      </td>
                      <td>
                        {dialer ? (
                          <Link
                            to={`/dialers/${dialer.id}`}
                            className="dialer-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="badge-sm badge-info">
                              <Phone size={10} style={{ marginRight: 4 }} />
                              {dialer.name}
                            </span>
                          </Link>
                        ) : (
                          <span className="text-muted text-sm">—</span>
                        )}
                      </td>
                      <td>
                        {licenseCount > 0 ? (
                          <span className="bot-count-badge">
                            {licenseCount}
                          </span>
                        ) : (
                          <span className="text-muted text-sm">—</span>
                        )}
                      </td>
                      <td>
                        {lastUsed ? (
                          <span className={`last-used-badge ${lastUsed.isActive ? 'active' : ''}`}>
                            {lastUsed.isActive && <span className="active-dot" />}
                            {lastUsed.text}
                          </span>
                        ) : (
                          <span className="text-muted text-sm">—</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge-sm ${(avatar.voice_mode || 'classic') === 'modern' ? 'badge-primary' : ''}`}>
                          {avatar.voice_mode || 'classic'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-sm ${(avatar.keyword_mode || 'legacy') === 'modern' ? 'badge-info' : ''}`}>
                          {avatar.keyword_mode || 'legacy'}
                        </span>
                      </td>
                      <td className="text-muted text-sm">
                        {avatar.voice_set_id ? getVoiceSetName(avatar.voice_set_id) : '-'}
                      </td>
                      <td>
                        <span className={`badge-sm ${avatar.archived ? 'badge-warning' : 'badge-success'}`}>
                          {avatar.archived ? 'Archived' : 'Active'}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="actions-cell">
                          <button
                            className="btn btn-icon btn-ghost btn-xs"
                            onClick={(e) => {
                              // Toggle off if already open
                              if (activeMenu?.id === avatar.id) {
                                setActiveMenu(null)
                                return
                              }
                              // Calculate position for fixed positioning
                              const rect = e.currentTarget.getBoundingClientRect()
                              const spaceBelow = window.innerHeight - rect.bottom
                              const shouldOpenUp = spaceBelow < 200
                              setActiveMenu({
                                id: avatar.id,
                                openUp: shouldOpenUp,
                                top: shouldOpenUp ? rect.top - 180 : rect.bottom + 4,
                                left: rect.left - 150
                              })
                            }}
                          >
                            <MoreVertical size={16} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  )
                })}
              </tbody>
            </table>

            {/* Fixed position dropdown menu - rendered outside table */}
            <AnimatePresence>
              {activeMenu && (
                <motion.div
                  className="dropdown-menu-fixed"
                  style={{
                    position: 'fixed',
                    top: activeMenu.top,
                    left: activeMenu.left,
                    zIndex: 9999
                  }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <button onClick={() => { navigate(`/avatars/${activeMenu.id}`); setActiveMenu(null) }}>
                    <Edit size={14} /> Open
                  </button>
                  <button onClick={() => { openModal('edit', avatars.find(a => a.id === activeMenu.id)); setActiveMenu(null) }}>
                    <Edit size={14} /> Edit
                  </button>
                  <button onClick={() => { openModal('clone', avatars.find(a => a.id === activeMenu.id)); setActiveMenu(null) }}>
                    <Copy size={14} /> Clone
                  </button>
                  <button onClick={() => { handleArchive(avatars.find(a => a.id === activeMenu.id)); setActiveMenu(null) }}>
                    {avatars.find(a => a.id === activeMenu.id)?.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                    {avatars.find(a => a.id === activeMenu.id)?.archived ? 'Restore' : 'Archive'}
                  </button>
                  <button className="danger" onClick={() => { openModal('delete', avatars.find(a => a.id === activeMenu.id)); setActiveMenu(null) }}>
                    <Trash2 size={14} /> Delete
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pagination */}
            <div className="pagination">
              <div className="pagination-info">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredAvatars.length)} of {filteredAvatars.length.toLocaleString()}
              </div>
              <div className="pagination-controls">
                <button
                  className="btn btn-icon btn-ghost btn-sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  <ChevronLeft size={16} /><ChevronLeft size={16} style={{ marginLeft: -8 }} />
                </button>
                <button
                  className="btn btn-icon btn-ghost btn-sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="pagination-current">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="btn btn-icon btn-ghost btn-sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  className="btn btn-icon btn-ghost btn-sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  <ChevronRight size={16} /><ChevronRight size={16} style={{ marginLeft: -8 }} />
                </button>
              </div>
            </div>
          </>
        )}
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2 className="modal-title">
                  {modalMode === 'add' && 'Create New Avatar'}
                  {modalMode === 'edit' && 'Edit Avatar'}
                  {modalMode === 'clone' && 'Clone Avatar'}
                  {modalMode === 'delete' && 'Delete Avatar'}
                </h2>
                <button className="modal-close" onClick={closeModal}>
                  <X size={20} />
                </button>
              </div>

              {modalMode === 'delete' ? (
                <div className="modal-body">
                  <p>Are you sure you want to delete <strong>{currentAvatar?.name}</strong>?</p>
                  <p className="text-muted mt-sm">This action cannot be undone.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="input-group">
                      <label className="input-label">Avatar Name</label>
                      <input
                        type="text"
                        className="input"
                        placeholder="Enter avatar name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>

                    <div className="input-group">
                      <label className="input-label">Voice Set</label>
                      <SearchableSelect
                        options={[
                          { value: '', label: 'Select a voice set...' },
                          ...voiceSets.map(vs => ({ value: String(vs.id), label: vs.name }))
                        ]}
                        value={String(formData.voice_set_id || '')}
                        onChange={(val) => setFormData({ ...formData, voice_set_id: val })}
                        placeholder="Select a voice set..."
                        field="voice_set_id"
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                      />
                    </div>
                  </div>
                </form>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={cloning}>
                  Cancel
                </button>
                {modalMode === 'delete' ? (
                  <button className="btn btn-danger" onClick={handleDelete}>
                    <Trash2 size={18} /> Delete
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={handleSubmit} disabled={cloning}>
                    {cloning ? (
                      <>
                        <Loader2 size={18} className="spin" /> Cloning...
                      </>
                    ) : (
                      <>
                        <Check size={18} />
                        {modalMode === 'add' && 'Create'}
                        {modalMode === 'edit' && 'Save'}
                        {modalMode === 'clone' && 'Clone'}
                      </>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Delete Modal */}
      <AnimatePresence>
        {showBulkDeleteModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)}
          >
            <motion.div
              className="modal"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2 className="modal-title">
                  <AlertTriangle size={20} style={{ color: 'var(--danger)' }} />
                  Bulk Delete Avatars
                </h2>
                <button className="modal-close" onClick={() => !bulkDeleting && setShowBulkDeleteModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body">
                <p>You are about to delete <strong>{selectedAvatars.length}</strong> avatars.</p>
                <p className="text-muted mt-sm">
                  Avatars with associated licenses will be skipped.
                  This action cannot be undone.
                </p>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={bulkDeleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? (
                    <>
                      <RefreshCw size={16} className="spin" /> Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} /> Delete {selectedAvatars.length} Avatars
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside to close menu */}
      {activeMenu && (
        <div
          className="menu-backdrop"
          onClick={() => setActiveMenu(null)}
        />
      )}

      {/* Toast Notification */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            className={`toast-notification ${toast.type}`}
            initial={{ opacity: 0, y: 50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 50, x: '-50%' }}
            style={{
              position: 'fixed',
              bottom: '24px',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '12px 24px',
              borderRadius: '8px',
              background: toast.type === 'error' ? 'var(--danger)' : '#22c55e',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              zIndex: 2000
            }}
          >
            {toast.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default Avatars